export type SoundKind = 'bubble_spawn' | 'bubble_touch' | 'bubble_merge' | 'bubble_split' | 'bubble_pop' | 'shell_pop' | 'energy_transfer' | 'ui_confirm' | 'journey_select' | 'journey_step';

class SoundManager {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private ambientNodes: OscillatorNode[] = [];
  private enabled = true;
  private ambientEnabled = true;
  private lastSoundAt = 0;

  async unlock(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = 0.16;
      this.master.connect(this.context.destination);
      this.ambientGain = this.context.createGain();
      this.ambientGain.gain.value = this.ambientEnabled ? 0.055 : 0;
      this.ambientGain.connect(this.master);
      this.startAmbient();
    }
    if (this.context.state === 'suspended') await this.context.resume();
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
    if (this.master && this.context) this.master.gain.setTargetAtTime(value ? 0.16 : 0, this.context.currentTime, 0.08);
  }

  setAmbient(value: boolean): void {
    this.ambientEnabled = value;
    if (this.ambientGain && this.context) this.ambientGain.gain.setTargetAtTime(value ? 0.055 : 0, this.context.currentTime, 0.4);
  }

  play(kind: SoundKind): void {
    if (!this.enabled || !this.context || !this.master) return;
    const nowMs = performance.now();
    if (nowMs - this.lastSoundAt < 70) return;
    this.lastSoundAt = nowMs;
    const now = this.context.currentTime;
    const recipes: Record<SoundKind, [number, number, number, OscillatorType]> = {
      bubble_spawn: [310, 620, 0.42, 'sine'], bubble_touch: [520, 450, 0.12, 'sine'],
      bubble_merge: [180, 310, 0.56, 'sine'], bubble_split: [460, 720, 0.28, 'triangle'],
      bubble_pop: [190, 82, 0.25, 'sine'], shell_pop: [680, 260, 0.46, 'triangle'],
      energy_transfer: [380, 840, 0.38, 'sine'], ui_confirm: [440, 660, 0.2, 'sine'],
      journey_select: [240, 590, 0.34, 'sine'], journey_step: [330, 880, 0.62, 'sine'],
    };
    const [from, to, duration, type] = recipes[kind];
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(to, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(kind.includes('pop') ? 0.45 : 0.22, now + 0.025);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.03);
  }

  private startAmbient(): void {
    if (!this.context || !this.ambientGain || this.ambientNodes.length) return;
    for (const frequency of [54, 81, 108]) {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.value = frequency === 54 ? 0.4 : 0.12;
      const drift = this.context.createOscillator();
      const driftGain = this.context.createGain();
      drift.frequency.value = 0.035 + frequency / 4200;
      driftGain.gain.value = frequency === 54 ? 1.8 : 0.8;
      drift.connect(driftGain).connect(oscillator.frequency);
      oscillator.connect(gain).connect(this.ambientGain);
      oscillator.start();
      drift.start();
      this.ambientNodes.push(oscillator, drift);
    }
  }
}

export const sound = new SoundManager();
